import { useState } from 'react'
import { useRouter } from 'next/router'
import { useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'

import Box from '@mui/material/Box'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { Navigation, Pagination, Autoplay, Lazy } from 'swiper'
import { SwiperSlide } from 'swiper/react'
import 'swiper/css'
import styled from '@emotion/styled'

import { ArticlePageStyledSwiper } from '../Banner/StyledSwiper'
import { useBanners } from 'utils/api/hooks'
import { sendBannerClick } from 'utils/hooks/analytics'
import { genPathByNavData } from 'utils/api'
import { selectWebview } from 'store/webview/webviewSlice'
import { useBannerGAForCarousel } from 'utils/hooks/useBannerGA'

const modules = [Navigation, Pagination, Autoplay, Lazy]

const Img = styled.img`
  width: 100%;
  border-radius: 12px;
  object-fit: cover;
  cursor: pointer;
`
const bannerSwiperId = uuidv4()
const Banner = ({
  banner,
  linkNamePrefix,
  onBannerClick,
  isFirst,
  isMobile,
}) => {
  if (isMobile) {
    return isFirst ? (
      <Img
        src={banner?.imageDouble?.url}
        onClick={onBannerClick}
        id={`${linkNamePrefix}-${banner._id}`}
      />
    ) : (
      <Img
        data-src={banner?.imageDouble?.url}
        onClick={onBannerClick}
        id={`${linkNamePrefix}-${banner._id}`}
        className="swiper-lazy"
      />
    )
  }

  return (
    <Img
      src={banner?.imageDouble?.url}
      onClick={onBannerClick}
      id={`${linkNamePrefix}-${banner._id}`}
    />
  )
}

const swiperSettings = {
  spaceBetween: 0,
  slidesPerView: 'auto',
  centeredSlides: true,
  loop: true,
  pagination: { el: '.banner-pagination', clickable: true },
  slideToClickedSlide: true,
}

const swiperAutoplay = {
  delay: 5000,
  disableOnInteraction: false,
}

const BannerCarousel = ({
  displayInfo,
  classification,
  isMobile,
  linkNamePrefix,
  bannerLocation,
}) => {
  const webview = useSelector(selectWebview)
  const [swiper, setSwiper] = useState(null)
  const [hovered, setHovered] = useState(false)
  const onNextClick = () => swiper?.slideNext()
  const onPrevClick = () => swiper?.slidePrev()

  const displayOpt = {
    displayAt: displayInfo,
    displayArticleClassificationIds: classification,
  }
  const { data: articleBanner, isLoading: articleBannerLoading } = useBanners(
    webview?.name,
    displayOpt
  )
  const { data: indexBanner, isLoading: indexBannerLoading } = useBanners(
    webview?.name
  )
  const router = useRouter()

  const displayBanner =
    displayInfo === 'articlepage'
      ? articleBanner
      : !!articleBanner?.length
      ? articleBanner
      : indexBanner?.slice(0, 1)

  const onBannerClick = (banner) => async () => {
    sendBannerClick({
      banner,
      bannerLocation,
    })

    const { path, isExternalLink } = await genPathByNavData(banner.navData)
    if (path) {
      if (isExternalLink) {
        window.open(path, '_blank')
      } else {
        router.push(path)
      }
    }
  }

  useBannerGAForCarousel({
    bannerData: displayBanner,
    bannerLocation,
    swiper,
    isRepeated: false,
  })

  useBannerGAForCarousel({
    bannerData: displayBanner,
    bannerLocation,
    swiper,
    isRepeated: true,
  })

  if (!displayBanner?.length) {
    return null
  }

  const swiperNavigation = {
    nextEl: `.instance-${bannerSwiperId} .banner-swiper-next`,
    prevEl: `.instance-${bannerSwiperId} .banner-swiper-prev`,
  }

  return articleBannerLoading ||
    indexBannerLoading ||
    !displayBanner.length ? null : (
    <Box
      sx={{ width: '100%' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <ArticlePageStyledSwiper
        modules={modules}
        {...swiperSettings}
        preloadImages={!isMobile}
        lazy={isMobile}
        watchSlidesProgress={isMobile}
        loopedSlides={displayBanner?.length}
        navigation={displayBanner?.length > 1 ? swiperNavigation : false}
        autoplay={displayBanner?.length > 1 ? swiperAutoplay : false}
        onAfterInit={(swiper) => setSwiper(swiper)}
      >
        {displayBanner?.length > 1 && !isMobile && hovered && (
          <Box className={`instance-${bannerSwiperId}`}>
            <div className="banner-swiper-next" onClick={onNextClick}>
              <ArrowForwardIosIcon fontSize="small" />
            </div>
            <div className="banner-swiper-prev" onClick={onPrevClick}>
              <ArrowBackIosIcon fontSize="small" />
            </div>
          </Box>
        )}
        <div className="banner-pagination" />
        {displayBanner?.map((banner, index) => (
          <SwiperSlide key={banner?._id}>
            <Banner
              banner={banner}
              onBannerClick={onBannerClick(banner)}
              isMobile={isMobile}
              linkNamePrefix={linkNamePrefix}
              isFirst={index === 0}
            />
          </SwiperSlide>
        ))}
      </ArticlePageStyledSwiper>
    </Box>
  )
}

export default BannerCarousel
